import {
    PLANNER_LEFT_COLUMNS,
    PLANNER_RIGHT_COLUMNS,
    PLANNER_HIDDEN_COLUMNS,
} from 'pages/dashboard/pages/updater-agent/pages/scheduler/components/week-view/plannerDefaultLayout'
import { applyLoadNotesToBuckets, loadNotesReceived } from './loadNotes'
import { ClientTrailer } from '@numeo/types'
import {
    ClientUser,
    DayViewLayout,
    DeliveryStatus,
    DriverFilter,
    OngoingLoadRoute,
    SchedulerQuery,
    SchedulerTruck,
    timeZonePreference,
} from '@numeo/types-core'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UpdaterMapTruck } from 'pages/dashboard/pages/updater-agent/pages/dashboard/types'
import type {
    GrossBoardColumnKey,
    GrossBoardLeftColumnKey,
    GrossBoardRightColumnKey,
} from 'pages/dashboard/pages/updater-agent/pages/scheduler/components/week-view/gridConfig'
import {
    ConfirmModalState,
    NewDriverStatus,
    ProcessedDriver,
    SchedulerOngoingLoad,
    SchedulerStatusCounts,
    SelectedDriverStatus,
} from 'pages/dashboard/pages/updater-agent/pages/scheduler/types'
import { getTruckId } from 'pages/dashboard/pages/updater-agent/pages/scheduler/utils/getTruckId'
import { RootState } from './index'
import { findSchedulerDriver, patchDriverStatusDelta, patchSchedulerDriverStatusReducer, patchDriverStatus } from './driverStatusMutation'
export interface TruckActiveLoad {
    truckId: string
    activeLoads: {
        _id: string
        sourceRateConfId?: string
        route: OngoingLoadRoute[]
        deliveryStatus: DeliveryStatus[]
    }[]
}

/**
 * The page size standing for "every row", which the server resolves to a bounded page
 * (SCHEDULER_MAX_ROWS_PER_REQUEST in main-service). Zero rather than a sentinel string so the whole set
 * stays numeric — the select's value, the request's `limit` and the persisted preference are one type.
 */
export const SCHEDULER_ALL_ROWS = 0

/**
 * Row counts the board's page-size select offers, in the order it lists them. The single source of truth:
 * the select renders these, and boardFilterSpecs validates a persisted preference against the same list, so
 * adding or dropping a size is one edit and cannot leave the two disagreeing.
 */
export const SCHEDULER_PAGE_SIZES = [20, 50, SCHEDULER_ALL_ROWS] as const

export type SchedulerPageSize = (typeof SCHEDULER_PAGE_SIZES)[number]

export const schedulerPageSizeLabel = (size: SchedulerPageSize): string => (size === SCHEDULER_ALL_ROWS ? 'All' : String(size))

interface SchedulerState {
    dispatcherFilterInitialized: boolean
    isLoadSidebarOpen: boolean
    loads: {
        assigned: SchedulerOngoingLoad[]
        inTransit: SchedulerOngoingLoad[]
        upcoming: SchedulerOngoingLoad[]
        loading: boolean
        error: string | null
        lastFetched: string | null
    }
    selectedLoad: SchedulerOngoingLoad | null
    unassigned: SchedulerOngoingLoad[]
    // Store a serializable anchor rectangle instead of a DOM element
    selectedLoadAnchorEl: { top: number; left: number; width: number; height: number } | null

    // Date settings
    selectedDate: string
    userTimeZone: timeZonePreference
    grossDateRange: { startDate: string; endDate: string } | null

    rcFilter: { nextWeek: boolean; endDate: boolean }
    viewType: 'day' | 'week' | 'month'
    grossBoardColumnPrefs: {
        leftOrder: GrossBoardLeftColumnKey[]
        rightOrder: GrossBoardRightColumnKey[]
        showDriverRate: boolean
        labels: Partial<Record<GrossBoardColumnKey, string>>
        collapsed: GrossBoardColumnKey[]
        removed: GrossBoardColumnKey[]
    }
    loadFormOpen?: boolean
    search?: SchedulerQuery
    processedDrivers: ProcessedDriver[]
    /**
     * Every row the active filters admit, across ALL pages, as the server computed it — `truck:<id>` for
     * a normal row, `driver:<id>` for a driver whose truck was deleted.
     * `null` means "the server did not scope anything" — kept distinct from an empty array, which is a
     * real filter that matched nobody. The header cards use this so they answer the same question the
     * rows do; see `get-drivers.ts` for why the client is told rather than re-implementing the filters.
     */
    visibleRowKeys: string[] | null
    isSidebarOpen: boolean
    assignedLoadsTruncated: boolean
    updatedTrucks: UpdaterMapTruck[]
    suggestedTrucks: SchedulerTruck[] | null
    trucksActiveLoads: TruckActiveLoad[]
    isSuggestionForLoad: SchedulerOngoingLoad | null

    // Modals
    showSettings: boolean
    showColumnsSettings: boolean

    /**
     * The signed-in user's own Update Board column layout, and whose it is (`project:user`). The store
     * outlives a logout and a company switch, so the owner is kept beside the layout: a layout loaded
     * for anyone else is not this user's, and the board waits for its own.
     *
     * `layout` is what the board draws and changes the moment the user moves something. `confirmed` is
     * the last value of each key the server is known to hold: what a change that could not be saved goes
     * back to.
     */
    dayViewLayout: { owner: string | null; layout: DayViewLayout; confirmed: DayViewLayout }

    trailers: {
        data: ClientTrailer[]
        loading: boolean
        error: string | null
        lastFetched: string | null
    }

    driverFilter: DriverFilter
    driverStatusFilter: string | null
    selectedDriverIds: Record<string, true>
    selectedTruckRow: { truck: SchedulerTruck | null; drivers: ClientUser[] } | null
    isTruckSidebarOpen: boolean
    isConfirmOpen: ConfirmModalState
    // Bumped every time a conflict opens the override modal. The pending-override payload
    // (newDriverStatus / selectedDriverStatus / selectedDriver) is a single global slot, so only
    // the most recent conflict is confirmable — this id lets each driver-status popover tell
    // whether what the modal is answering is its own 409 or a later one from another cell.
    confirmRequestId: number
    // Verdict of an override retry, tagged with the conflict it answered. The slot itself can be
    // taken over by a newer conflict while a retry is still in flight, so the popover waiting on
    // that retry has to read its outcome from here rather than from isConfirmOpen.
    overrideResult: { requestId: number; succeeded: boolean } | null
    newDriverStatus: NewDriverStatus | null
    selectedDriverStatus: SelectedDriverStatus | null
    selectedDriver: ClientUser | null
    viewPagination: { skip: number; limit: number; total: number }
    statusCounts: SchedulerStatusCounts
    // Load mutations and explicit board refreshes invalidate load lists.
    loadsRefreshNonce: number
    // Bumped after a client-side driver-status mutation to force a counts/rows refetch,
    // since server-computed statusCounts can't be recomputed client-side (pagination).
    dataRefreshNonce: number
}

const initialState: SchedulerState = {
    dispatcherFilterInitialized: false,
    isLoadSidebarOpen: false,
    loads: {
        assigned: [],
        inTransit: [],
        upcoming: [],
        loading: false,
        error: null,
        lastFetched: null,
    },

    unassigned: [],
    selectedDate: new Date().toISOString(),
    userTimeZone: 'UTC' as timeZonePreference,
    grossDateRange: null,

    rcFilter: { nextWeek: false, endDate: true },
    viewType: 'day',
    grossBoardColumnPrefs: {
        leftOrder: PLANNER_LEFT_COLUMNS,
        rightOrder: PLANNER_RIGHT_COLUMNS,
        showDriverRate: true,
        labels: {},
        collapsed: [],
        removed: PLANNER_HIDDEN_COLUMNS,
    },
    loadFormOpen: false,
    isSidebarOpen: false,
    selectedLoad: null,
    selectedLoadAnchorEl: null,
    search: {
        search: '',
        driver: [],
        dispatcher: [],
        driverStatus: [],
    },
    processedDrivers: [],
    visibleRowKeys: null,
    updatedTrucks: [],
    assignedLoadsTruncated: false,
    suggestedTrucks: null,
    trucksActiveLoads: [],
    isSuggestionForLoad: null,

    // modals
    showSettings: false,
    showColumnsSettings: false,

    dayViewLayout: { owner: null, layout: {}, confirmed: {} },

    trailers: {
        data: [],
        loading: false,
        error: null,
        lastFetched: null,
    },

    driverFilter: DriverFilter.WITH_TRUCKS,
    driverStatusFilter: null,
    selectedDriverIds: {},
    selectedTruckRow: null,
    isTruckSidebarOpen: false,
    isConfirmOpen: ConfirmModalState.CLOSED,
    confirmRequestId: 0,
    overrideResult: null,
    newDriverStatus: null,
    selectedDriverStatus: null,
    selectedDriver: null,
    viewPagination: {
        skip: 0,
        limit: 50,
        total: 0,
    },
    statusCounts: {
        total: 0,
        availableCount: 0,
        onRouteCount: 0,
        preBookedCount: 0,
        enRouteCount: 0,
        arrivedCount: 0,
        customStatusCounts: {},
    },
    loadsRefreshNonce: 0,
    dataRefreshNonce: 0,
}

const getSchedulerLoadIdentity = (load: SchedulerOngoingLoad): string | null => {
    const id = load.id ?? load.sourceRateConfId
    return id ? String(id) : null
}

/**
 * Merge a fresh API load over one already held in the scheduler, keeping the scheduler-only flags the
 * API response knows nothing about.
 *
 * Those flags are restored BY NAME rather than left to the spread: a spread preserves only the keys the
 * response omits entirely, so an explicit `isSuggested: undefined` from the API would clobber the local
 * value. `??` keeps an explicitly provided flag winning while an absent one falls back to what we hold.
 */
const mergeSchedulerLoad = (existingLoad: SchedulerOngoingLoad, incomingLoad: SchedulerOngoingLoad): SchedulerOngoingLoad => ({
    ...existingLoad,
    ...incomingLoad,
    isSuggested: incomingLoad.isSuggested ?? existingLoad.isSuggested,
})

const mergeSchedulerLoads = (currentLoads: SchedulerOngoingLoad[], incomingLoads: SchedulerOngoingLoad[]): SchedulerOngoingLoad[] => {
    const nextLoads = [...currentLoads]
    const indexById = new Map<string, number>()

    nextLoads.forEach((load, index) => {
        const id = getSchedulerLoadIdentity(load)
        if (!id || indexById.has(id)) return
        indexById.set(id, index)
    })

    incomingLoads.forEach((load) => {
        const id = getSchedulerLoadIdentity(load)
        if (!id) {
            nextLoads.push(load)
            return
        }

        const existingIndex = indexById.get(id)
        if (existingIndex === undefined) {
            indexById.set(id, nextLoads.length)
            nextLoads.push(load)
            return
        }

        nextLoads[existingIndex] = load
    })

    return nextLoads
}

const applyDispatchStatus = (state: SchedulerState, driverIds: string[], dispatchStatusId: string | null) => {
    const ids = new Set(driverIds.map(String))
    for (const row of state.processedDrivers) {
        for (const driver of row.drivers ?? []) {
            if (ids.has(String(driver.id))) driver.dispatchStatusId = dispatchStatusId
        }
    }
}

const withLayoutKeys = (layout: DayViewLayout, changes: DayViewLayout): DayViewLayout => {
    const next: Record<string, unknown> = { ...layout }
    for (const [key, value] of Object.entries(changes)) {
        if (value === undefined) delete next[key]
        else next[key] = value
    }
    return next
}

const schedulerSlice = createSlice({
    name: 'scheduler',
    initialState,
    reducers: {
        setAssignedLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            // Replace the list while still deduping repeated loads that can arrive within the same payload.
            state.loads.assigned = mergeSchedulerLoads([], action.payload)
            state.loads.error = null
            state.loads.lastFetched = new Date().toISOString()
        },
        appendAssignedLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            state.loads.assigned = mergeSchedulerLoads(state.loads.assigned, action.payload)
            state.loads.error = null
            state.loads.lastFetched = new Date().toISOString()
        },
        setLoadsLoading: (state, action: PayloadAction<boolean>) => {
            state.loads.loading = action.payload
        },
        setInTransitLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            state.loads.inTransit = action.payload
        },
        setUpcomingLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            state.loads.upcoming = action.payload
        },
        updateAssignedLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            const nextAssigned = [...state.loads.assigned]
            const nextInTransit = [...state.loads.inTransit]
            const nextUpcoming = [...state.loads.upcoming]

            action.payload.forEach((incomingLoad) => {
                const targetId = incomingLoad.id ?? incomingLoad.sourceRateConfId
                if (!targetId) return

                const matcher = (l: SchedulerOngoingLoad) => (l.id ?? l.sourceRateConfId) === targetId

                // Assigned bucket (week view)
                const assignedIdx = nextAssigned.findIndex(matcher)
                if (assignedIdx >= 0) {
                    nextAssigned[assignedIdx] = incomingLoad
                } else {
                    nextAssigned.push(incomingLoad)
                }

                // Day view buckets: only update existing entries to preserve server-side filters
                const inTransitIdx = nextInTransit.findIndex(matcher)
                if (inTransitIdx >= 0) {
                    nextInTransit[inTransitIdx] = incomingLoad
                }

                const upcomingIdx = nextUpcoming.findIndex(matcher)
                if (upcomingIdx >= 0) {
                    nextUpcoming[upcomingIdx] = incomingLoad
                }
            })

            state.loads.assigned = nextAssigned
            state.loads.inTransit = nextInTransit
            state.loads.upcoming = nextUpcoming
        },
        /**
         * Refresh loads in every bucket that already holds them, WITHOUT inserting anywhere.
         *
         * `updateAssignedLoads` deliberately pushes an unmatched load into `assigned` — correct when the
         * caller knows the load belongs on the week board (a week fetch, a drag-drop assignment). It is
         * wrong for an edit made from a surface that is not the week board: the load sidebar is shared by
         * Week View and Day View, and a Day View load lives in `inTransit`/`upcoming`, so pushing it into
         * `assigned` would put a load on the week board that the week query never returned. Use this when
         * the intent is only "re-render what is already on screen with fresh data".
         *
         * It also does not MOVE a load between buckets when its status changes, and deliberately so.
         * `inTransit`/`upcoming` are status-scoped *fetches*, but nothing renders bucket membership as
         * status — every read path re-filters on `load.status` (`useDayViewTableRows`, `useCurrentLoad`,
         * `WeekView`'s `getCurrentStatusLoadForTruckLoads`), and the patched entry carries the API's fresh
         * status, so a load that no longer qualifies drops out of the row on the next render without being
         * re-bucketed. Re-bucketing here would cost a refetch per edit to fix nothing.
         */
        patchExistingLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            action.payload.forEach((incomingLoad) => {
                const targetId = incomingLoad.id ?? incomingLoad.sourceRateConfId
                if (!targetId) return

                const matcher = (l: SchedulerOngoingLoad) => (l.id ?? l.sourceRateConfId) === targetId

                const buckets: SchedulerOngoingLoad[][] = [state.loads.assigned, state.loads.inTransit, state.loads.upcoming, state.unassigned]
                buckets.forEach((bucket) => {
                    const idx = bucket.findIndex(matcher)
                    if (idx >= 0) bucket[idx] = mergeSchedulerLoad(bucket[idx], incomingLoad)
                })

                // The sidebar resolves its load through selectFreshSelectedLoad, which falls back to
                // `selectedLoad` when no bucket holds it — patch it so the sidebar never shows stale data.
                if (state.selectedLoad && matcher(state.selectedLoad)) {
                    state.selectedLoad = mergeSchedulerLoad(state.selectedLoad, incomingLoad)
                }
            })
        },
        setRCFilter: (state, action: PayloadAction<{ nextWeek: boolean; endDate: boolean }>) => {
            state.rcFilter = action.payload
        },

        setUnassignedLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            state.unassigned = action.payload
        },
        appendUnassignedLoads: (state, action: PayloadAction<SchedulerOngoingLoad[]>) => {
            state.unassigned = mergeSchedulerLoads(state.unassigned, action.payload)
        },
        upsertAssignedLoad: (state, action: PayloadAction<SchedulerOngoingLoad>) => {
            const incomingLoad = action.payload
            const targetId = incomingLoad.id ?? incomingLoad.sourceRateConfId
            if (!targetId) return

            const matcher = (l: SchedulerOngoingLoad) => (l.id ?? l.sourceRateConfId) === targetId

            const nextAssigned = [...state.loads.assigned]
            const assignedIdx = nextAssigned.findIndex(matcher)
            if (assignedIdx >= 0) {
                nextAssigned[assignedIdx] = incomingLoad
            } else {
                nextAssigned.push(incomingLoad)
            }

            const nextInTransit = [...state.loads.inTransit]
            const inTransitIdx = nextInTransit.findIndex(matcher)
            if (inTransitIdx >= 0) {
                nextInTransit[inTransitIdx] = incomingLoad
            }

            const nextUpcoming = [...state.loads.upcoming]
            const upcomingIdx = nextUpcoming.findIndex(matcher)
            if (upcomingIdx >= 0) {
                nextUpcoming[upcomingIdx] = incomingLoad
            }

            state.loads.assigned = nextAssigned
            state.loads.inTransit = nextInTransit
            state.loads.upcoming = nextUpcoming
        },
        removeAssignedLoadById: (state, action: PayloadAction<string>) => {
            state.loads.assigned = state.loads.assigned.filter((l) => (l.id ?? l.sourceRateConfId) !== action.payload)
        },
        removeLoadFromSchedulerBucketsById: (state, action: PayloadAction<string>) => {
            const targetId = action.payload
            const matcher = (load: SchedulerOngoingLoad) => (load.id ?? load.sourceRateConfId) !== targetId
            state.loads.assigned = state.loads.assigned.filter(matcher)
            state.loads.inTransit = state.loads.inTransit.filter(matcher)
            state.loads.upcoming = state.loads.upcoming.filter(matcher)
        },
        addUnassignedLoad: (state, action: PayloadAction<SchedulerOngoingLoad>) => {
            const load = action.payload
            const targetId = load.id ?? load.sourceRateConfId
            const exists = state.unassigned.some((l) => (l.id ?? l.sourceRateConfId) === targetId)
            if (!exists) {
                state.unassigned.push(load)
            }
        },
        removeUnassignedLoadById: (state, action: PayloadAction<string>) => {
            const targetId = action.payload
            state.unassigned = state.unassigned.filter((l) => (l.id ?? l.sourceRateConfId) !== targetId)
        },
        // Date/View Actions
        setSelectedDate: (state, action: PayloadAction<string>) => {
            state.selectedDate = action.payload
        },
        setViewType: (state, action: PayloadAction<'day' | 'week' | 'month'>) => {
            state.viewType = action.payload
        },

        setLoadFormOpen: (state, action: PayloadAction<boolean>) => {
            state.loadFormOpen = action.payload
        },
        setSearch: (state, action: PayloadAction<SchedulerQuery>) => {
            state.search = action.payload
        },
        setDispatcherFilterInitialized: (state, action: PayloadAction<boolean>) => {
            state.dispatcherFilterInitialized = action.payload
        },
        setIsSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isSidebarOpen = action.payload
            if (action.payload) {
                state.selectedLoad = null
                state.isLoadSidebarOpen = false
                state.isTruckSidebarOpen = false
                state.selectedTruckRow = null
            }
        },
        setIsLoadSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isLoadSidebarOpen = action.payload
        },
        setShowSettings: (state, action: PayloadAction<boolean>) => {
            state.showSettings = action.payload
        },
        setShowColumnsSettings: (state, action: PayloadAction<boolean>) => {
            state.showColumnsSettings = action.payload
        },
        /** A layout as loaded from the server, which makes it the confirmed one as well. */
        setDayViewLayout: (state, action: PayloadAction<{ owner: string | null; layout: DayViewLayout }>) => {
            state.dayViewLayout = { ...action.payload, confirmed: action.payload.layout }
        },
        /** Applies some layout keys on top of the loaded layout; a key set to `undefined` is removed. */
        mergeDayViewLayout: (state, action: PayloadAction<DayViewLayout>) => {
            state.dayViewLayout.layout = withLayoutKeys(state.dayViewLayout.layout, action.payload)
        },
        /** Records that the server holds these keys; a key set to `undefined` is one it does not hold. */
        confirmDayViewLayout: (state, action: PayloadAction<DayViewLayout>) => {
            state.dayViewLayout.confirmed = withLayoutKeys(state.dayViewLayout.confirmed, action.payload)
        },
        setGrossBoardColumnPrefs: (state, action: PayloadAction<SchedulerState['grossBoardColumnPrefs']>) => {
            state.grossBoardColumnPrefs = action.payload
        },
        setAssignedLoadsTruncated: (state, action: PayloadAction<boolean>) => {
            state.assignedLoadsTruncated = action.payload
        },
        setSelectedLoad: (state, action: PayloadAction<SchedulerOngoingLoad | null>) => {
            if (state.isSidebarOpen) {
                state.isSidebarOpen = false
            }
            state.selectedLoad = action.payload
        },
        setSelectedLoadAnchorEl: (state, action: PayloadAction<{ top: number; left: number; width: number; height: number } | null>) => {
            state.selectedLoadAnchorEl = action.payload
        },
        setProcessedDrivers: (state, action: PayloadAction<ProcessedDriver[]>) => {
            state.processedDrivers = action.payload
        },
        /**
         * Patch one driver's dispatch status in place after a manual change on the board.
         *
         * The write endpoint answers with just the driver id and the new value rather than the whole
         * Application, so the row is refreshed from here instead of a refetch — the board polls, and
         * pulling the fleet again on every dropdown click would be a page-sized request per keystroke.
         */
        setDriverDispatchStatus: (state, action: PayloadAction<{ driverId: string; dispatchStatusId: string | null }>) => {
            applyDispatchStatus(state, [action.payload.driverId], action.payload.dispatchStatusId)
        },
        /** Several drivers at once — one assignment sets every driver on the truck. */
        setDriversDispatchStatus: (state, action: PayloadAction<{ driverIds: string[]; dispatchStatusId: string | null }>) => {
            applyDispatchStatus(state, action.payload.driverIds, action.payload.dispatchStatusId)
        },
        setVisibleRowKeys: (state, action: PayloadAction<string[] | null>) => {
            state.visibleRowKeys = action.payload
        },
        setUpdatedTrucks: (state, action: PayloadAction<UpdaterMapTruck[]>) => {
            state.updatedTrucks = action.payload
        },
        setSuggestedTrucks: (state, action: PayloadAction<SchedulerTruck[] | null>) => {
            state.suggestedTrucks = action.payload
        },
        setTrucksActiveLoads: (state, action: PayloadAction<TruckActiveLoad[]>) => {
            state.trucksActiveLoads = action.payload
        },
        setSuggestionForLoad: (state, action: PayloadAction<SchedulerOngoingLoad | null>) => {
            state.isSuggestionForLoad = action.payload
        },
        setUserTimeZone: (state, action: PayloadAction<timeZonePreference>) => {
            state.userTimeZone = action.payload
        },
        setGrossDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string } | null>) => {
            state.grossDateRange = action.payload
        },
        setTrailers: (state, action: PayloadAction<ClientTrailer[]>) => {
            state.trailers.data = action.payload
            state.trailers.loading = false
            state.trailers.error = null
            state.trailers.lastFetched = new Date().toISOString()
        },
        setDriverFilter: (state, action: PayloadAction<DriverFilter>) => {
            state.driverFilter = action.payload
        },
        setDriverStatusFilter: (state, action: PayloadAction<string | null>) => {
            state.driverStatusFilter = action.payload
            // The chip is applied server-side before pagination, so the result set changes — go back to page 1.
            state.viewPagination = { ...state.viewPagination, skip: 0 }
        },
        setSelectedDriverIds: (state, action: PayloadAction<string[]>) => {
            state.selectedDriverIds = action.payload.reduce<Record<string, true>>((selected, driverId) => {
                selected[driverId] = true
                return selected
            }, {})
        },
        toggleSelectedDriverIds: (state, action: PayloadAction<string[]>) => {
            const driverIds = action.payload
            const allSelected = driverIds.length > 0 && driverIds.every((driverId) => state.selectedDriverIds[driverId])
            for (const driverId of driverIds) {
                if (allSelected) delete state.selectedDriverIds[driverId]
                else state.selectedDriverIds[driverId] = true
            }
        },
        setIsConfirmOpen: (state, action: PayloadAction<ConfirmModalState>) => {
            state.isConfirmOpen = action.payload
            // A fresh conflict takes ownership of the pending-override slot from any earlier one.
            if (action.payload === ConfirmModalState.OPEN) {
                state.confirmRequestId += 1
            }
        },
        setOverrideResult: (state, action: PayloadAction<{ requestId: number; succeeded: boolean } | null>) => {
            state.overrideResult = action.payload
        },
        setNewDriverStatus: (state, action: PayloadAction<NewDriverStatus | null>) => {
            state.newDriverStatus = action.payload
        },
        setSelectedDriverStatus: (state, action: PayloadAction<SelectedDriverStatus | null>) => {
            state.selectedDriverStatus = action.payload
        },
        setSelectedDriver: (state, action: PayloadAction<ClientUser | null>) => {
            state.selectedDriver = action.payload
        },
        setViewPagination: (state, action: PayloadAction<{ skip: number; limit: number; total: number }>) => {
            state.viewPagination = action.payload
        },
        setSchedulerStatusCounts: (state, action: PayloadAction<SchedulerStatusCounts>) => {
            state.statusCounts = action.payload
        },
        refreshDriverStatusRows: (state) => {
            state.dataRefreshNonce += 1
        },
        bumpSchedulerDataRefresh: (state) => {
            state.loadsRefreshNonce += 1
            state.dataRefreshNonce += 1
        },
        requestSchedulerDataRefresh: (state) => {
            state.loadsRefreshNonce += 1
            state.dataRefreshNonce += 1
            state.loads.loading = true
        },
        setSelectedTruckRow: (state, action: PayloadAction<{ truck: SchedulerTruck | null; drivers: ClientUser[] } | null>) => {
            state.selectedTruckRow = action.payload
            state.isTruckSidebarOpen = action.payload !== null
        },
        setIsTruckSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.isTruckSidebarOpen = action.payload
            if (!action.payload) state.selectedTruckRow = null
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadNotesReceived, (state, { payload }) => {
            applyLoadNotesToBuckets(
                [state.loads.assigned, state.loads.inTransit, state.loads.upcoming, state.unassigned, ...(state.selectedLoad ? [[state.selectedLoad]] : [])],
                payload
            )
        })

        builder.addCase(patchDriverStatusDelta, patchSchedulerDriverStatusReducer)
        builder.addCase(patchDriverStatus, patchSchedulerDriverStatusReducer)
    },
})

// Export actions
export const {
    setAssignedLoads,
    appendAssignedLoads,
    setInTransitLoads,
    setUpcomingLoads,
    setUnassignedLoads,
    appendUnassignedLoads,
    upsertAssignedLoad,
    removeAssignedLoadById,
    removeLoadFromSchedulerBucketsById,
    addUnassignedLoad,
    removeUnassignedLoadById,
    setSelectedDate,
    setViewType,
    setLoadFormOpen,
    setSearch,
    setAssignedLoadsTruncated,
    setIsSidebarOpen,
    setShowSettings,
    setShowColumnsSettings,
    setDayViewLayout,
    mergeDayViewLayout,
    confirmDayViewLayout,
    setGrossBoardColumnPrefs,
    setSelectedLoad,
    setSelectedLoadAnchorEl,
    setProcessedDrivers,
    setDriverDispatchStatus,
    setDriversDispatchStatus,
    setVisibleRowKeys,
    setUpdatedTrucks,
    setSuggestedTrucks,
    setRCFilter,
    setTrucksActiveLoads,
    setSuggestionForLoad,
    setUserTimeZone,
    setGrossDateRange,
    setTrailers,
    setDriverFilter,
    setDriverStatusFilter,
    setSelectedDriverIds,
    toggleSelectedDriverIds,
    setIsConfirmOpen,
    setOverrideResult,
    setNewDriverStatus,
    setSelectedDriverStatus,
    setSelectedDriver,
    setIsLoadSidebarOpen,
    setLoadsLoading,
    setViewPagination,
    setSchedulerStatusCounts,
    bumpSchedulerDataRefresh,
    refreshDriverStatusRows,
    requestSchedulerDataRefresh,
    updateAssignedLoads,
    patchExistingLoads,
    setSelectedTruckRow,
    setIsTruckSidebarOpen,
    setDispatcherFilterInitialized,
} = schedulerSlice.actions

//
export const selectDispatcherFilterInitialized = (state: RootState) => state.scheduler.dispatcherFilterInitialized
export const selectSelectedDate = (state: RootState) => state.scheduler.selectedDate
export const selectUserTimeZone = (state: RootState) => state.scheduler.userTimeZone
export const selectGrossDateRange = (state: RootState) => state.scheduler.grossDateRange

// Selectors
const selectLoads = (state: RootState) => state.scheduler.loads
export const selectViewType = (state: RootState) => state.scheduler.viewType
export const selectGrossBoardColumnPrefs = (state: RootState) => state.scheduler.grossBoardColumnPrefs

export const selectAssignedLoads = createSelector([selectLoads], (loads) => loads.assigned)
const selectInTransitLoads = createSelector([selectLoads], (loads) => loads.inTransit)
const selectUpcomingLoads = createSelector([selectLoads], (loads) => loads.upcoming)
export const selectUnassignedLoads = (state: RootState) => state.scheduler.unassigned

/**
 * Returns combined in-transit + upcoming loads for day view.
 * Components that use this selector won't re-render when only assignedLoads changes,
 * preventing the cascade of DriverStatusDropdown re-renders after ETA batch refresh.
 */
export const selectDayViewLoads = createSelector([selectInTransitLoads, selectUpcomingLoads], (inTransit, upcoming) => [
    ...(inTransit ?? []),
    ...(upcoming ?? []),
])
export const selectLoadsLoading = (state: RootState) => state.scheduler.loads.loading

export const selectLoadFormOpen = (state: RootState) => state.scheduler.loadFormOpen
export const selectSearch = (state: RootState) => state.scheduler.search
export const selectAssignedLoadsTruncated = (state: RootState) => state.scheduler.assignedLoadsTruncated
export const selectIsSidebarOpen = (state: RootState) => state.scheduler.isSidebarOpen
export const selectIsLoadSidebarOpen = (state: RootState) => state.scheduler.isLoadSidebarOpen
export const selectSelectedLoad = (state: RootState) => state.scheduler.selectedLoad
export const selectSelectedLoadAnchorEl = (state: RootState) => state.scheduler.selectedLoadAnchorEl

/**
 * Returns the currently-selected load resolved against the latest bucket data
 * (assigned / in-transit / upcoming / unassigned). The raw `selectedLoad` is a
 * snapshot frozen when the user clicked the row, so it goes stale when ETA or
 * delivery status batch updates land in the buckets. Consumers like the load
 * sidebar should use this selector to stay in sync with the table.
 */
export const selectFreshSelectedLoad = createSelector(
    [selectSelectedLoad, selectLoads, selectUnassignedLoads],
    (selectedLoad, loads, unassigned): SchedulerOngoingLoad | null => {
        if (!selectedLoad) return null
        const targetId = selectedLoad.id ?? selectedLoad.sourceRateConfId
        if (!targetId) return selectedLoad
        const matcher = (l: SchedulerOngoingLoad) => (l.id ?? l.sourceRateConfId) === targetId
        return loads.assigned.find(matcher) ?? loads.inTransit.find(matcher) ?? loads.upcoming.find(matcher) ?? unassigned.find(matcher) ?? selectedLoad
    }
)
export const selectProcessedDrivers = (state: RootState) => state.scheduler.processedDrivers
/** A driver as the board's rows carry them, with their live `driverStatus` (see `findSchedulerDriver`). */
export const selectSchedulerDriverById = (state: RootState, driverId: string | undefined) => findSchedulerDriver(state.scheduler.processedDrivers, driverId)
/**
 * How many rows the board actually holds — what the footer counts as displayed. Read instead of deriving it
 * from `limit`, because the "All" size is capped server-side: with a fleet above that ceiling the response is
 * a prefix, and `skip + limit` would claim rows that were never sent.
 */
export const selectProcessedDriverCount = (state: RootState) => state.scheduler.processedDrivers.length
export const selectVisibleRowKeys = (state: RootState) => state.scheduler.visibleRowKeys
export const selectUpdatedTrucks = (state: RootState) => state.scheduler.updatedTrucks

/**
 * processedDrivers with each truck enriched by locationString from updatedTrucks (ELD).
 * Merge key: getTruckId(truck) so scheduler trucks and ELD trucks match.
 */
export const selectProcessedDriversWithTruckLocation = createSelector(
    [selectProcessedDrivers, selectUpdatedTrucks],
    (drivers, updatedTrucks): ProcessedDriver[] => {
        if (!drivers?.length) return drivers ?? []

        const locationByTruckId = new Map<string, string>()
        for (const truck of updatedTrucks ?? []) {
            const truckId = getTruckId(truck)
            if (truckId && truck.locationString) locationByTruckId.set(truckId, truck.locationString)
        }

        return drivers.map((driver) => {
            if (!driver.truck) return driver
            const truckId = getTruckId(driver.truck)
            const locationString = truckId ? locationByTruckId.get(truckId) : undefined
            if (locationString == null) return driver
            return { ...driver, truck: { ...driver.truck, locationString } }
        })
    }
)
export const selectTrucksActiveLoads = (state: RootState) => state.scheduler.trucksActiveLoads
export const selectRCFilter = (state: RootState) => state.scheduler.rcFilter
export const selectSuggestionForLoad = (state: RootState) => state.scheduler.isSuggestionForLoad
export const selectDriverFilter = (state: RootState) => state.scheduler.driverFilter
export const selectDriverStatusFilter = (state: RootState) => state.scheduler.driverStatusFilter
export const selectNewDriverStatus = (state: RootState) => state.scheduler.newDriverStatus
export const selectSelectedDriverStatus = (state: RootState) => state.scheduler.selectedDriverStatus
export const selectSelectedDriver = (state: RootState) => state.scheduler.selectedDriver
export const selectViewPagination = (state: RootState) => state.scheduler.viewPagination
export const selectSchedulerStatusCounts = (state: RootState) => state.scheduler.statusCounts
export const selectSchedulerLoadsRefreshNonce = (state: RootState) => state.scheduler.loadsRefreshNonce
export const selectSchedulerDataRefreshNonce = (state: RootState) => state.scheduler.dataRefreshNonce

// Modals selectors
export const selectShowSettings = (state: RootState) => state.scheduler.showSettings
export const selectShowColumnsSettings = (state: RootState) => state.scheduler.showColumnsSettings
export const selectDayViewLayout = (state: RootState) => state.scheduler.dayViewLayout
export const selectTrailers = (state: RootState) => state.scheduler.trailers
export const selectSelectedDriverIds = (state: RootState) => state.scheduler.selectedDriverIds
export const selectIsConfirmOpen = (state: RootState) => state.scheduler.isConfirmOpen
export const selectConfirmRequestId = (state: RootState) => state.scheduler.confirmRequestId
export const selectOverrideResult = (state: RootState) => state.scheduler.overrideResult
export const selectSelectedTruckRow = (state: RootState) => state.scheduler.selectedTruckRow
export const selectIsTruckSidebarOpen = (state: RootState) => state.scheduler.isTruckSidebarOpen

export default schedulerSlice.reducer
